import { inject, Injectable } from '@angular/core';

import { PersistDataService } from './app-state/persist-data.service';
import { ExtractedValue, ExtractedValuesByKey } from '../models/extracted-values';
import { ExistingAuftrag } from '../pages/consolidation-page/components/existing-auftraege-selection/model/existing-alt-auftrag.interface';
import { toGermanDate } from '../utils/Date.utils';
import { CtBatchStoreService } from './app-state/ct-batch-store.service';

export enum PrefillSource {
  ALTFALL = 'ALTFALL',
  BEAUFTRAGUNG = 'BEAUFTRAGUNG',
}

export interface PrefillResults {
  fields: Partial<ExtractedValuesByKey>;
  sources: Record<string, PrefillSource>;
}

/**
 * Bridges the Altfall picked in the assignment step to the consolidation step. Owns both ends of the
 * mapping so the field keys live in exactly one place.
 */
@Injectable({
  providedIn: 'root',
})
export class PrefillService {
  private readonly persistDataService = inject(PersistDataService);
  private readonly ctBatchStoreService = inject(CtBatchStoreService);

  store(auftrag: ExistingAuftrag) {
    this.persistDataService.altfallZuweisung = {
      auftragId: auftrag.id,
      isLegacySystemAuftrag: auftrag.isLegacySystemAuftrag,
      fields: mapToConsolidationFields(auftrag),
    };
  }

  clear() {
    this.persistDataService.altfallZuweisung = null;
  }

  /**
   * The Altfall is the master record of the case, so its values win over what the extraction read
   * from the document. Only where the Altfall itself is empty does the extracted value survive.
   * Reports what it applied so the form can mark those fields.
   */
  applyTo(extracted: Partial<ExtractedValuesByKey>): PrefillResults {
    const results: PrefillResults = {
      fields: {},
      sources: {},
    };

    const metaData = this.ctBatchStoreService.ctBatch.metaData;
    const metaDataFields: ExtractedValuesByKey = {
      kopfdaten_vorname_betroffene_person: metaData?.betroffenePerson?.vorname ?? null,
      kopfdaten_nachname_betroffene_person: metaData?.betroffenePerson?.nachname ?? null,
      kopfdaten_geburtsdatum: toGermanDate(metaData?.betroffenePerson?.geburtsdatum),
      kopfdaten_schadendatum: toGermanDate(metaData?.schadenDatum),
    };
    this.addPrefillData(PrefillSource.BEAUFTRAGUNG, results, metaDataFields);

    const altfallFields = this.persistDataService.altfallZuweisung?.fields;
    this.addPrefillData(PrefillSource.ALTFALL, results, altfallFields);

    this.addPrefillData(undefined, results, extracted);

    return results;
  }

  private addPrefillData(
    source: PrefillSource | undefined,
    results: PrefillResults,
    data: Partial<ExtractedValuesByKey> | undefined,
  ) {
    if (!data) {
      return;
    }

    for (const [key, value] of Object.entries(data)) {
      if (isEmpty(value) || !!results.fields[key]) {
        continue;
      }

      results.fields[key] = value;

      if (source) {
        results.sources[key] = source;
      }
    }
  }
}

const isEmpty = (value: ExtractedValue | undefined): boolean =>
  value === undefined || value === null || value === '';

/**
 * Excludes the Forderungspositionen, everything else the consolidation form asks for is covered.
 * Note that kopfdaten_forderungsdatum belongs to the single invoice rather than the case, so in a
 * split batch every consolidation page gets the same date from the Altfall and may need a manual fix.
 */
function mapToConsolidationFields(auftrag: ExistingAuftrag): ExtractedValuesByKey {
  return {
    kopfdaten_vorname_betroffene_person: auftrag.claimant?.firstName ?? null,
    kopfdaten_nachname_betroffene_person: auftrag.claimant?.lastName ?? null,
    kopfdaten_geburtsdatum: toGermanDate(auftrag.claimant?.birthDate),
    kopfdaten_schadendatum: toGermanDate(auftrag.damageDate),
    kopfdaten_forderungsdatum: toGermanDate(auftrag.invoiceDate),
    kopfdaten_kostentraegerzeichen: auftrag.kst?.sign ?? null,
    kostentraeger_name: auftrag.kst?.name ?? null,
    kostentraeger_strasse: auftrag.kst?.street ?? null,
    kostentraeger_hausnummer: auftrag.kst?.houseNumber ?? null,
    kostentraeger_postleitzahl: auftrag.kst?.zip ?? null,
    kostentraeger_stadt: auftrag.kst?.city ?? null,
  };
}
